import { Pencil, Users } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { UserDetail } from '@/api/admin-types'
import type { Branch, DualControlResult } from '@/api/types'
import { ProblemAlert } from '@/components/error-surface'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription } from '@/lib/branch'

import { AdminDualControlNotice, AdminDualControlOutcome } from './dual-control'
import { useAssignUserRoles, useUpdateUser } from './mutations'
import { ADMIN_PERMISSIONS, useAdminPermissions } from './permissions'
import { useAdminBranches, useAdminRoles } from './queries'

/**
 * Amending a user's profile. Whole-record, like `LoanProductForm`: the profile is sent in full on
 * every amendment rather than as a partial patch, so there is no field this dialog forgot and left
 * behind.
 */
export function EditProfileDialog({
  user,
  open,
  onOpenChange,
}: {
  user: UserDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useAdminPermissions()
  const branches = useAdminBranches()
  const update = useUpdateUser(user.userId)

  const [fullName, setFullName] = useState(user.fullName)
  const [email, setEmail] = useState(user.email ?? '')
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '')
  const [branchId, setBranchId] = useState(user.branchId)
  const [isLoanOfficer, setIsLoanOfficer] = useState(user.isLoanOfficer)
  const [isTeller, setIsTeller] = useState(user.isTeller)
  const [result, setResult] = useState<DualControlResult | null>(null)

  useEffect(() => {
    if (open) {
      setFullName(user.fullName)
      setEmail(user.email ?? '')
      setPhoneNumber(user.phoneNumber ?? '')
      setBranchId(user.branchId)
      setIsLoanOfficer(user.isLoanOfficer)
      setIsTeller(user.isTeller)
      setResult(null)
      update.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user])

  const trimmedName = fullName.trim()
  const trimmedEmail = email.trim()
  const ready = trimmedName !== '' && trimmedEmail !== '' && branchId !== ''
  const settled = result !== null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="size-4" aria-hidden />
            Amend profile
          </DialogTitle>
          <DialogDescription>
            Username and employee code are fixed at creation and are not amended here.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {settled ? (
            <AdminDualControlOutcome
              result={result}
              awaitingDescription="This amendment is waiting for a countersignature. The profile still reads as it did before."
              appliedDescription="The profile is amended."
            />
          ) : (
            <>
              <AdminDualControlNotice
                action="Amending this profile"
                approvePermission={ADMIN_PERMISSIONS.userUpdateApprove}
                canCountersign={permissions.canCountersignUpdateUser}
              />

              <div className="space-y-1.5">
                <Label htmlFor="edit-user-name">Full name</Label>
                <Input id="edit-user-name" value={fullName} onChange={(event) => setFullName(event.target.value)} />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="edit-user-email">Email</Label>
                  <Input
                    id="edit-user-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="edit-user-phone">Phone (optional)</Label>
                  <Input
                    id="edit-user-phone"
                    value={phoneNumber}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="edit-user-branch">Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger id="edit-user-branch">
                    <SelectValue placeholder="Select a branch…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(branches.data ?? []).map((branch: Branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branchDescription(branch)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-body">
                  <Checkbox
                    checked={isLoanOfficer}
                    onCheckedChange={(checked) => setIsLoanOfficer(checked === true)}
                  />
                  Loan officer
                </label>
                <label className="flex items-center gap-2 text-sm text-body">
                  <Checkbox checked={isTeller} onCheckedChange={(checked) => setIsTeller(checked === true)} />
                  Teller
                </label>
              </div>

              {update.isError ? <ProblemAlert error={update.error} /> : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {settled ? (
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!ready || update.isPending}
                onClick={() => {
                  if (!ready) return
                  update.mutate(
                    {
                      fullName: trimmedName,
                      email: trimmedEmail,
                      phoneNumber: phoneNumber.trim() === '' ? null : phoneNumber.trim(),
                      branchId,
                      isLoanOfficer,
                      isTeller,
                    },
                    { onSuccess: setResult },
                  )
                }}
              >
                {update.isPending ? 'Working…' : 'Submit the amendment'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Replacing a user's roles.
 *
 * The server refuses a combination it flags as a segregation-of-duties conflict unless a
 * justification is given, so the field is offered up front rather than added after a refusal —
 * left blank, it is sent as `null`, which is what "no justification was needed" means.
 */
export function EditRolesDialog({
  user,
  open,
  onOpenChange,
}: {
  user: UserDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useAdminPermissions()
  const roles = useAdminRoles()
  const assign = useAssignUserRoles(user.userId)

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [justification, setJustification] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  useEffect(() => {
    if (open) {
      // UserDetail.roles is a list of role names, not ids — the user endpoint carries no role id
      // on the account itself. Matched by name against the role register rather than invented, and
      // a name this build cannot find a role for (renamed or deleted since) simply pre-checks
      // nothing for it, which is the honest reading rather than a guess at a stale id.
      const currentIds = new Set(
        (roles.data ?? [])
          .filter((role) => user.roles.includes(role.name))
          .map((role) => role.roleId),
      )
      setSelected(currentIds)
      setJustification('')
      setResult(null)
      assign.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roles.data, user.roles])

  const settled = result !== null

  const toggle = (roleId: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(roleId)) {
        next.delete(roleId)
      } else {
        next.add(roleId)
      }
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="size-4" aria-hidden />
            Edit roles
          </DialogTitle>
          <DialogDescription>
            The whole set is replaced — a role left unchecked here is removed from the account, not
            merely left alone.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {settled ? (
            <AdminDualControlOutcome
              result={result}
              awaitingDescription="This role change is waiting for a countersignature. The account still holds its current roles."
              appliedDescription="The account's roles are updated."
            />
          ) : (
            <>
              <AdminDualControlNotice
                action="Changing this account's roles"
                approvePermission={ADMIN_PERMISSIONS.roleAssignApprove}
                canCountersign={permissions.canCountersignAssignRoles}
              />

              {roles.isPending ? (
                <p className="text-sm text-muted">Loading roles…</p>
              ) : roles.isError ? (
                <ProblemAlert error={roles.error} />
              ) : (
                <ul className="max-h-64 space-y-1.5 overflow-y-auto">
                  {(roles.data ?? []).map((role) => (
                    <li key={role.roleId}>
                      <label className="flex items-start gap-2 rounded-control border border-border-subtle p-2 text-sm">
                        <Checkbox
                          checked={selected.has(role.roleId)}
                          onCheckedChange={() => toggle(role.roleId)}
                          className="mt-0.5"
                        />
                        <span className="min-w-0">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-heading">{role.name}</span>
                            {role.forbidsFinancialPermissions ? (
                              <Badge tone="neutral">non-financial only</Badge>
                            ) : null}
                            {role.isBreakGlass ? (
                              // Assignable here, unlike at creation — checking it skips the
                              // conflict matrix and is written to the audit trail as a bypass.
                              <Badge tone="warning">break-glass — assignment is audited</Badge>
                            ) : null}
                          </span>
                          <span className="block text-xs text-muted">{role.description}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="role-justification">Segregation-of-duties justification (if asked for)</Label>
                <Input
                  id="role-justification"
                  value={justification}
                  autoComplete="off"
                  onChange={(event) => setJustification(event.target.value)}
                />
                <p className="text-xs text-muted">
                  Only needed if the combination you have chosen conflicts with the server&apos;s
                  segregation-of-duties rules — it refuses the change and names the conflict if so.
                </p>
              </div>

              {assign.isError ? <ProblemAlert error={assign.error} /> : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          {settled ? (
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={assign.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={assign.isPending}
                onClick={() => {
                  assign.mutate(
                    {
                      roleIds: [...selected],
                      segregationJustification: justification.trim() === '' ? null : justification.trim(),
                    },
                    { onSuccess: setResult },
                  )
                }}
              >
                {assign.isPending ? 'Working…' : 'Submit the role change'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
